import React, { useState } from "react";
import loadable from "@loadable/component";
import useWebToAppCampaign, {
    CampaignOptions,
} from "web/react/hooks/use-web-to-app-campaign/use-web-to-app-campaign";
import ContinueToOption from "web/react/components/continue-to-option/continue-to-option";
import { gettext, gettextNoop } from "web/script/modules/django-i18n";
import PaywallMarkup from "web/react/components/paywall-markup/paywall-markup";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import history from "web/script/utils/history";

const SendToAppChecklistModal = loadable(
    () =>
        import(
            /* webpackChunkName: "send-to-app-checklist-modal" */ "web/react/components/send-to-app-checklist-modal/send-to-app-checklist-modal"
        )
);

const SendToAppModal = loadable(
    () =>
        import(
            /* webpackChunkName: "send-to-app-modal" */ "web/react/components/send-to-app-modal/send-to-app-modal"
        )
);

// must add all usable titles to this array so they are included in the bundled translations
const TITLE_KEYS = [
    gettextNoop("feed.show_more.app_overlay_block.title_4"),
    gettextNoop("product_card.app_overlay_block.title_1"),
];

interface WebToAppCampaignProps {
    children: React.ReactNode;
    options: CampaignOptions;
    beforeOnClick?: () => void;
    onCampaignNotActive: (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | MouseEvent
    ) => void;
    onContinueClick?: () => void;
    analyticsEventLabel: string;
    id?: string;
    productImageUrl?: string;
    continueOptionHref?: string;
}

export default function WebToAppCampaign({
    children,
    options,
    beforeOnClick,
    onCampaignNotActive,
    onContinueClick,
    analyticsEventLabel,
    productImageUrl,
    id = "",
    continueOptionHref = "",
}: WebToAppCampaignProps): JSX.Element {
    // WebToAppCampaigns with the same `analyticsEventLabel` require an `id` to avoid mismatched overlays
    const modalName = `modal-${analyticsEventLabel}-${id}`;
    const paywalledClassName = analyticsEventLabel;
    const modalIsOpen = Boolean(useOverlayWatcher(modalName));
    const [hasBeenBlocked, setHasBeenBlocked] = useState<boolean>(false);
    const {
        campaignDetails,
        onLoad,
        onClick: onCtaClick,
        fetchCampaign,
    } = useWebToAppCampaign(
        "block_modal",
        {
            hasBeenBlocked,
            ...options,
        },
        false
    );

    const titleKey = campaignDetails?.titleKey || "";
    if (titleKey && !TITLE_KEYS.includes(titleKey)) {
        console.error("Invalid titleKey");
    }

    function openModal(): void {
        // check overlay state to avoid delay caused by hooks
        if (history.getCurrentState().overlay !== modalName) {
            history.pushOverlay(modalName);
        }
    }

    function onClose(): void {
        history.popOverlay(modalName);
    }

    function onClick(event): void {
        // we must persist the event so it can be used after the handler has run
        event.preventDefault();
        event.persist?.();

        // run anything, regardless of the campaign decision - useful for sending
        // analytics data related to the wrapped component
        beforeOnClick?.();
        fetchCampaign().then((campaignDetailsData) => {
            if (campaignDetailsData) {
                openModal();

                if (!hasBeenBlocked) {
                    setHasBeenBlocked(true);
                    onLoad();
                }
            } else {
                onCampaignNotActive(event);
            }
        });
    }

    let continueOption;
    if (campaignDetails?.blockType === "soft_block" && (onContinueClick || continueOptionHref)) {
        continueOption = (
            <ContinueToOption
                href={continueOptionHref}
                continueMessage={gettext("general.continue_shopping_btn_text")}
                analyticsEventLabel={analyticsEventLabel}
                continueClick={() => {
                    onContinueClick?.();
                    onClose();
                }}
            />
        );
    }

    return (
        <>
            <span className={paywalledClassName} onClick={onClick}>
                {children}
            </span>
            {campaignDetails?.componentType === "checklist_modal" && (
                <SendToAppChecklistModal
                    url={campaignDetails.branchLink}
                    isOpen={modalIsOpen}
                    onClose={onClose}
                    analyticsEventLabel={analyticsEventLabel}
                    onCtaClick={onCtaClick}
                />
            )}
            {campaignDetails?.componentType === "generic_modal" && (
                <SendToAppModal
                    title={gettext(titleKey)}
                    url={campaignDetails.branchLink}
                    isOpen={modalIsOpen}
                    onClose={onClose}
                    analyticsEventLabel={analyticsEventLabel}
                    onCtaClick={onCtaClick}
                    productImageURL={productImageUrl}
                    extraContent={(): JSX.Element => {
                        return continueOption;
                    }}
                />
            )}
            <PaywallMarkup cssSelector={paywalledClassName} />
        </>
    );
}
